import React, { useMemo, useState } from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const SAMPLE_TEXT = `::question::
Скільки рук у людини?

[ ] Одна
[x] Дві
[ ] Три
[ ] П'ять

::question::
Скільки рук у собаки?

[x] Нуль
[ ] Дві
[ ] Три
[ ] П'ять`;

const CHATGPT_PROMPT = `Переформатуй мій текст у шаблон для імпорту тестів. Поверни тільки готовий шаблон без пояснень.

Правила:
- кожне питання починай з рядка ::question::
- після нього додай текст питання
- варіанти відповіді пиши окремими рядками
- правильний варіант позначай [x]
- неправильні варіанти позначай [ ]
- у кожному питанні має бути рівно один правильний варіант

Приклад:
::question::
Скільки рук у людини?

[ ] Одна
[x] Дві
[ ] Три
[ ] П'ять

Ось текст, який треба переформатувати:
`;

const OPTION_IDS = ["А", "Б", "В", "Г", "Д", "Е", "Є", "Ж", "З", "И"];

const makeId = () => Math.floor(Math.random() * 1_000_000);

export const parseBulkTestQuestions = (source) => {
    const text = String(source || "").replace(/\r\n/g, "\n").trim();
    if (!text) return { questions: [], error: "Вставте текст із питаннями." };

    const blocks = text
        .split(/^::question::\s*$/gim)
        .map((block) => block.trim())
        .filter(Boolean);

    if (!blocks.length) {
        return { questions: [], error: "Не знайдено жодного блоку ::question::." };
    }

    const questions = [];

    for (let blockIdx = 0; blockIdx < blocks.length; blockIdx += 1) {
        const lines = blocks[blockIdx]
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean);
        const firstOptionIdx = lines.findIndex((line) => /^\[(x|\s)\]\s+/i.test(line));

        if (firstOptionIdx <= 0) {
            return {
                questions: [],
                error: `Питання #${blockIdx + 1}: додайте текст питання і варіанти у форматі [ ] або [x].`,
            };
        }

        const questionText = lines.slice(0, firstOptionIdx).join("<br />");
        const optionLines = lines.slice(firstOptionIdx);
        const options = [];
        let correctAnswer = "";

        for (let optionIdx = 0; optionIdx < optionLines.length; optionIdx += 1) {
            const match = optionLines[optionIdx].match(/^\[(x|\s)\]\s+(.+)$/i);
            if (!match) {
                return {
                    questions: [],
                    error: `Питання #${blockIdx + 1}: варіант "${optionLines[optionIdx]}" має починатися з [ ] або [x].`,
                };
            }

            const optionId = OPTION_IDS[optionIdx] || String(optionIdx + 1);
            options.push({ id: optionId, text: match[2].trim() });

            if (match[1].toLowerCase() === "x") {
                if (correctAnswer) {
                    return {
                        questions: [],
                        error: `Питання #${blockIdx + 1}: має бути тільки один правильний варіант [x].`,
                    };
                }
                correctAnswer = optionId;
            }
        }

        if (options.length < 2) {
            return { questions: [], error: `Питання #${blockIdx + 1}: додайте мінімум два варіанти.` };
        }

        if (!correctAnswer) {
            return { questions: [], error: `Питання #${blockIdx + 1}: позначте правильну відповідь через [x].` };
        }

        questions.push({
            id: makeId(),
            question: questionText,
            options,
            correctAnswer,
            type: "test",
            subtitle: "",
            imgPath: "",
            videoPath: "",
            addClass: "",
        });
    }

    return { questions, error: "" };
};

const BulkTestQuestionsDialog = ({ open, onClose, onAdd }) => {
    const [source, setSource] = useState(SAMPLE_TEXT);
    const [helpOpen, setHelpOpen] = useState(false);
    const parsed = useMemo(() => parseBulkTestQuestions(source), [source]);

    function handleAdd() {
        if (parsed.error || !parsed.questions.length) return;
        onAdd(parsed.questions);
        onClose();
    }

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
                <DialogTitle>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                        <Typography variant="h6" fontWeight={700}>
                            Додати багато тестів
                        </Typography>
                        <Tooltip title="Показати шаблон і промпт для ChatGPT">
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<HelpOutlineIcon />}
                                onClick={() => setHelpOpen(true)}
                            >
                                Як користуватись
                            </Button>
                        </Tooltip>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Шаблон тестів"
                            value={source}
                            onChange={(e) => setSource(e.target.value)}
                            fullWidth
                            multiline
                            minRows={16}
                            helperText="Кожне питання починається з ::question::. Правильний варіант позначається [x]."
                        />

                        {parsed.error ? (
                            <Alert severity="error">{parsed.error}</Alert>
                        ) : (
                            <Alert severity="success">Готово до додавання: {parsed.questions.length} тестів.</Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose}>Скасувати</Button>
                    <Button variant="contained" onClick={handleAdd} disabled={Boolean(parsed.error)}>
                        Додати тести
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Як користуватись імпортом тестів</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2">
                            Вставте один або кілька тестів. Кожен тест починається з рядка ::question::,
                            далі йде текст питання, а потім варіанти відповідей.
                        </Typography>
                        <Typography variant="body2">
                            Неправильні варіанти починаються з [ ], правильний варіант починається з [x].
                            У кожному питанні має бути тільки один правильний варіант.
                        </Typography>
                        <TextField
                            label="Приклад шаблону"
                            value={SAMPLE_TEXT}
                            fullWidth
                            multiline
                            minRows={10}
                            InputProps={{ readOnly: true }}
                        />
                        <TextField
                            label="Промпт для ChatGPT"
                            value={CHATGPT_PROMPT}
                            fullWidth
                            multiline
                            minRows={12}
                            InputProps={{ readOnly: true }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHelpOpen(false)}>Закрити</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BulkTestQuestionsDialog;
